import React, { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Divider, Button, Group, Title, createStyles, Box, Image } from '@mantine/core';
import CategoryImageDropzone from './CategoryImageDropzone';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { saveCategory, updateCategory } from '../../services/api.service';

// Custom styles
const useStyles = createStyles((theme) => ({
	imageContainer: {
		border: '1px solid #CCCCCC',
		padding: 3,
		borderRadius: theme.radius.sm,
	}
}));

const CategoryForm = ({ closeModal, isEdit, category }) => {

	// Hooks
	const [isLoading, toggleLoading] = useState(false);
	const [fileURL, setFileURL] = useState('');
	const [isLogoChanged, toggleIsLogoChanged] = useState(false);
	const { classes } = useStyles();
	const form = useForm({
		initialValues: {
			name: '',
			image: null,
		},
	});

	// Check if edit
	useEffect(() => {
		if (isEdit) form.setValues({
			name: category.name,
			image: category.image,
		});
		// eslint-disable-next-line
	}, []);

	// Submit form
	const onSubmit = (values) => {

		// Set loader
		toggleLoading(true);

		// Conditions
		if (!isEdit) {

			// Call API
			saveCategory(values, function () {

				// Show success
				notifications.show({
					title: 'Categoría creada correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		} else {

			// Call API
			updateCategory(category.id, values, function () {

				// Show success
				notifications.show({
					title: 'Categoría modificada correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		}

	}

	// Process accepted file
	const onDrop = (file) => {
		form.setFieldValue('image', file[0]);
		setFileURL(URL.createObjectURL(file[0]));
		toggleIsLogoChanged(true);
	}

	// Reject file and show error
	const onReject = (file) => {
		notifications.show({
			title: 'Error',
			message: 'Este tipo de archivo no está soportado.',
			color: 'red'
		});
	}

	return (
		<>
			{/* Form */}
			<form onSubmit={form.onSubmit(onSubmit)}>
				{/* Title */}
				<Title
					order={5}
					mb="md"
				>
					{isEdit ? 'Modificar' : ''} Información de Categoría
				</Title>
				{/* Input Group */}
				<Group grow>
					{/* Name */}
					<TextInput
						required
						label="Nombre"
						placeholder="Nombre de la categoría"
						value={form.values.name}
						onChange={(event) => form.setFieldValue('name', event.currentTarget.value)}
						{...form.getInputProps('name')}
					/>
				</Group>
				<Divider
					my="md"
				/>
				{/* Subtitle */}
				<Title
					order={5}
					mb="md"
				>
					Imagen
				</Title>
				{/* Dropzone */}
				<Group>
					{/* Zone */}
					<CategoryImageDropzone
						onDrop={onDrop}
						onReject={onReject}
					/>
					{/* Preview */}
					{form.values.image !== null ?
						<Box className={classes.imageContainer}>
							<Image
								withPlaceholder
								src={isEdit && !isLogoChanged ? category.image : fileURL}
								imageProps={isEdit && !isLogoChanged ? null : { onLoad: () => URL.revokeObjectURL(fileURL) }}
								height={148}
								width={148}
								radius="sm"
							/>
						</Box>
					: null}
				</Group>
				{/* Buttons */}
				<Group
					position="apart"
					grow={!isEdit}
				>
					{/* Cancel */}
					<Button
						mt="xl"
						color="red"
						onClick={closeModal}
						disabled={isLoading}
						variant="light"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						Cancelar
					</Button>
					{/* Submit */}
					<Button
						mt="xl"
						type="submit"
						loading={isLoading}
						variant="filled"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						{isEdit ? 'Guardar Cambios' : 'Crear Categoría'}
					</Button>
				</Group>
			</form>
		</>
	);

}

export default CategoryForm;
